/**
 * detectRigFamily — name the generator family from the rig's OWN bone-name
 * signature, so family-keyed knowledge (authored joint frames, priors) can
 * reach a file nobody hand-labelled.
 *
 * Signature, never metadata (the ratified posture): a generator tag in a file
 * header is laundered by any round trip — the default Blender "Send To"
 * bridge strips it — while the bone names ARE the rig. This is deliberately
 * the narrow version of ADR-004's signature detection: it answers only for
 * families we have measured and have knowledge to apply, and returns null
 * ("say nothing") for everything else, because a WRONG family is worse than
 * no family — it would promote another generator's authored frames to top
 * authority over this rig's own measured geometry.
 *
 * Depends on: ir/rig-descriptor (type only). Depended on by: the app's import
 * door (which declares the family into the Document).
 */
import type { RigDescriptor } from '../ir/rig-descriptor.js';

/**
 * One family's intrinsic tell. `required` names must ALL be present — a
 * partial match is not a match, because these prefixes are how the family is
 * recognised at all.
 */
interface FamilySignature {
  readonly family: string;
  readonly required: readonly RegExp[];
  readonly minMatches: number;
  readonly pattern: RegExp;
}

const SIGNATURES: readonly FamilySignature[] = [
  {
    // Both corpus Meshy generations share reversed spine numbering, lowercase
    // neck and headfront. Generic unprefixed humanoids must not match.
    family: 'meshy',
    required: [/^Hips$/, /^Spine02$/, /^Spine01$/, /^Spine$/, /^neck$/, /^headfront$/, /^LeftArm$/, /^RightArm$/],
    minMatches: 8,
    pattern: /^(?:Hips|Spine02|Spine01|Spine|neck|headfront|LeftArm|RightArm)$/,
  },
  {
    // VRoid Studio exports (VRM 0.x and 1.0 alike) name every humanoid bone
    // `J_Bip_<side>_<bone>` and every secondary `J_Sec_*`. Measured across
    // 273 unique VRM in the local corpus: universal.
    family: 'vrm',
    required: [/^J_Bip_C_Hips$/, /^J_Bip_C_Head$/],
    minMatches: 12,
    pattern: /^J_Bip_/,
  },
  {
    // Mixamo's auto-rigger prefixes every bone `mixamorig` (with occasional
    // numbered variants normalised upstream). Detected so the SOURCE side of
    // a retarget can carry authored arm frames — the twist alignment pairs
    // secondaries across sides, and both must speak the same semantic.
    family: 'mixamo',
    required: [/^mixamorig\d*Hips$/, /^mixamorig\d*Head$/],
    minMatches: 12,
    pattern: /^mixamorig/,
  },
  {
    // Second Life / OpenSim canonical skeleton (avatar_skeleton.xml): every
    // joint is `m`-prefixed CamelCase (`mPelvis`, `mCollarLeft`, Bento
    // `mHandThumb1Left`). Detected so the retarget can key authored SL joint
    // frames when they land (near-collinear Z-up limbs — the same
    // ill-conditioned-witness class the VRM/Tripo frames cure); until then
    // the family is a report. The lowercase-m pattern cannot collide with
    // `mixamorig*` (its next letter is lowercase).
    family: 'sl',
    required: [/^mPelvis$/, /^mTorso$/, /^mChest$/, /^mHipLeft$/, /^mHipRight$/],
    minMatches: 12,
    pattern: /^m[A-Z]/,
  },
  {
    // Current-generation Tripo auto-rig: `Hip` (singular) + `Waist` +
    // `L_/R_Upperarm` + clavicles, and the 18 mesh-deform twist supplements
    // (`L_ThighTwist01` …) the 2025 update added. The twist gate is
    // deliberate: the authored tripo limb frames were measured on
    // current-gen rigs only (8-rig corpus, T and A pose), and a pre-update
    // rig without supplements is an unmeasured population — say nothing.
    family: 'tripo',
    required: [/^Hip$/, /^Waist$/, /^L_Upperarm$/, /^R_Upperarm$/, /^L_Clavicle$/, /^R_Clavicle$/],
    minMatches: 8,
    pattern: /^[LR]_(?:Thigh|Calf|Upperarm|Forearm)Twist\d{2}$/,
  },
];

/**
 * The family this rig's own names declare, or null when nothing matches
 * confidently. Case-sensitive on purpose: these are exact generator strings,
 * and a case-insensitive match would start guessing.
 */
export function detectRigFamily(rig: RigDescriptor): string | null {
  const names = rig.bones.map((b) => b.name);
  for (const sig of SIGNATURES) {
    const hasAll = sig.required.every((re) => names.some((n) => re.test(n)));
    if (!hasAll) continue;
    if (names.filter((n) => sig.pattern.test(n)).length < sig.minMatches) continue;
    return sig.family;
  }
  return null;
}
